import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { get as commsGet } from "../../../service/comms/commsApi";
import {
  getOversightRooms,
  classifyOversightError,
} from "../../../service/comms/oversightAnalyticsService";
import { useOversightAccess } from "../../../hooks/comms/OversightAccessProvider";
import { EmptyState } from "../../../common/comms/EmptyState";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { SkeletonRows } from "../../../common/comms/SkeletonRows";
import { CoachMultiSelect } from "./CoachMultiSelect";
import { RoomCard } from "./RoomCard";

const PAGE_SIZE = 25;

/**
 * Rooms browser tab for the `/coaches` oversight console.
 *
 * Reads its own `?coach_id=` deep-link filter (one or more repeated params,
 * e.g. set by "View rooms" on the Coaches tab, or by the `CoachMultiSelect`
 * picker rendered here) and fetches `GET /oversight/rooms` scoped to those
 * coach ids. The URL is the single source of truth for the selection —
 * `CoachMultiSelect` is controlled from `?coach_id=` and reports selection
 * changes back via `onChange`, which rewrites the same params. Manager
 * scope-narrowing is enforced server-side, so no client-side visibility
 * filtering is needed here.
 *
 * List is offset-paginated ("Load more", page size 25) and kept in API
 * order (last activity desc).
 */
export function RoomsTab() {
  const navigate = useNavigate();
  const { reprobe } = useOversightAccess();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedIds = searchParams.getAll("coach_id");
  const selectedKey = selectedIds.join(",");

  const [rooms, setRooms] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  /** @type {[('gateway'|'other'|'forbidden'|null), Function]} */
  const [errorKind, setErrorKind] = useState(null);
  /** @type {[('gateway'|'other'|null), Function]} */
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Initial/filter-change/retry fetch — always starts at offset 0. Keyed off
  // `selectedKey` (a stable string) rather than `selectedIds` (a new array
  // every render) so this only re-runs when the filter value actually
  // changes; `selectedIds` read inside the effect matches `selectedKey` on
  // every render where the effect fires.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setErrorKind(null);
    setLoadMoreError(null);
    getOversightRooms(commsGet, { limit: PAGE_SIZE, offset: 0, coach_id: selectedIds })
      .then((payload) => {
        if (!active) return;
        const list = Array.isArray(payload?.rooms) ? payload.rooms : [];
        setRooms(list);
        setOffset(0);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch((err) => {
        if (!active) return;
        const kind = classifyOversightError(err);
        setErrorKind(kind);
        setRooms([]);
        setHasMore(false);
        if (kind === "forbidden") {
          reprobe();
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, reloadToken, reprobe]);

  const handleRetry = () => setReloadToken((t) => t + 1);

  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    const nextOffset = offset + PAGE_SIZE;
    setLoadingMore(true);
    setLoadMoreError(null);
    getOversightRooms(commsGet, { limit: PAGE_SIZE, offset: nextOffset, coach_id: selectedIds })
      .then((payload) => {
        if (!mountedRef.current) return;
        const list = Array.isArray(payload?.rooms) ? payload.rooms : [];
        setRooms((cur) => [...cur, ...list]);
        setOffset(nextOffset);
        setHasMore(list.length >= PAGE_SIZE);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        const kind = classifyOversightError(err);
        if (kind === "forbidden") {
          reprobe();
          return;
        }
        setLoadMoreError(kind);
      })
      .finally(() => {
        if (mountedRef.current) setLoadingMore(false);
      });
  };

  // Rewrites `?coach_id=` to `nextIds` (repeated params), preserving every
  // other search param. The fetch effect above is keyed off `selectedKey`,
  // so this URL write is what actually triggers the offset-0 refetch.
  const handleCoachFilterChange = (nextIds) => {
    const next = new URLSearchParams(searchParams);
    next.delete("coach_id");
    nextIds.forEach((id) => next.append("coach_id", id));
    setSearchParams(next);
  };

  const handleOpen = (roomId) => {
    navigate(`/oversight/${roomId}`);
  };

  return (
    <div>
      <CoachMultiSelect selectedIds={selectedIds} onChange={handleCoachFilterChange} />

      {errorKind === "forbidden" ? null : errorKind === "gateway" ? (
        <RetryBanner onRetry={handleRetry} />
      ) : errorKind === "other" ? (
        <RetryBanner message="Something went wrong loading rooms." onRetry={handleRetry} />
      ) : loading ? (
        <SkeletonRows variant="cards" count={6} />
      ) : rooms.length === 0 ? (
        <EmptyState title="No active coaching rooms in your scope" />
      ) : (
        <>
          <div className="space-y-3">
            {rooms.map((room) => (
              <RoomCard key={room?.room_id} room={room} onOpen={handleOpen} />
            ))}
          </div>

          {loadMoreError && (
            <div className="mt-3">
              <RetryBanner
                message={
                  loadMoreError === "gateway"
                    ? "Couldn't load more rooms."
                    : "Something went wrong loading more rooms."
                }
                onRetry={handleLoadMore}
              />
            </div>
          )}

          {hasMore && !loadMoreError && (
            <div className="flex justify-center mt-4">
              <button
                type="button"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="min-h-[44px] px-4 text-sm font-medium text-gray-600 rounded-lg border border-gray-200 bg-white hover:bg-orange-50 disabled:opacity-50"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default RoomsTab;
